/**
 * How many jars of this honey, in this size (#401).
 *
 * Reported from a device, testing Send Honey to Vendor:
 *
 *   "there is no 'quantities' with the bottle sizes so you can't tell at
 *    a glance the numbers of each jar and varietal available anywhere in
 *    the Honey Pool screens. To be clear you can see how many pounds of
 *    honey of each varietal."
 *
 * Pounds are what the pool holds; JARS are what is on the shelf, and the
 * shelf is what somebody is standing in front of when they fill a vendor
 * order. The app could answer "25 lb of Orange" and not "how many 1 lb
 * jars of Orange", which is the only form of the question anybody asks
 * while packing a box.
 *
 * ── WHICH NARROWS WHICH ──
 *
 * The varietal narrows the SIZES, not the other way round. Web had this
 * inverted: it put the count on the varietal options and could only
 * compute one once a size had been chosen, so the picker opened with no
 * numbers on it at all and the first choice was still blind. That is
 * exactly the shape #384 found and fixed for bottling -- the fix there
 * was to invert the dependency, not to swap the fields.
 *
 * So: choose the honey, then see which jars it is in and how many.
 *
 * ── ONE SOURCE, NOT TWO ──
 *
 * Everything here is derived from `bottled_by_varietal`, which the pool
 * response already carries and both platforms already load. The size
 * total is the sum across varietals rather than a second figure fetched
 * separately, because two numbers from two queries can disagree and the
 * one on screen would have no way to say which it was.
 *
 * It also folds a NULL varietal in as its own row, which the by_varietal
 * block on /honey/bottled-stock deliberately does NOT -- that one
 * excludes NULL because "honey nobody separated is not honey of every
 * kind". Correct there, wrong for a total, which is the other reason the
 * total comes from here.
 *
 * ── UNKNOWN IS NOT ZERO ──
 *
 * A null split means the request has not answered yet. Every function
 * returns null for that, and the callers render nothing. A zero we did
 * not verify reads as "none left" and would stop somebody sending jars
 * they are holding.
 *
 * MUST AGREE WITH mobile/src/services/bottledStock.js -- registered as a
 * pair in platformParity.test.js. Two clients disagreeing about how many
 * jars are on a shelf is worse than neither knowing.
 */

/** Jars of every varietal in this size, or null if the split has not loaded. */
export function jarsInSize(split, sizeId) {
  if (!split) return null;
  return split
    .filter((r) => String(r.bottle_size_id) === String(sizeId))
    .reduce((total, r) => total + Number(r.bottles || 0), 0);
}

/**
 * Jars of ONE varietal in this size, or null if the split has not loaded.
 *
 * `varietalId` null is compared as null rather than skipped: on an
 * account with no varietal row actually named General, null IS General,
 * and the split carries those jars under a null varietal_id. Treating it
 * as "nothing selected" would report zero for the commonest case there
 * is.
 */
export function jarsOfVarietalInSize(split, sizeId, varietalId) {
  if (!split) return null;
  const row = split.find(
    (r) => String(r.bottle_size_id) === String(sizeId)
      && (r.varietal_id ?? null) === (varietalId ?? null),
  );
  // Present but absent from the split means zero: that query drops a
  // combination whose net is nil, and we know it loaded.
  return row ? Number(row.bottles || 0) : 0;
}

/**
 * What to put beside a jar size, given what is chosen so far.
 *
 * BOTH NUMBERS once a varietal is chosen. A bare count that silently
 * changes meaning when the picker moves is how somebody sends the wrong
 * jars; "3 of 12" answers what was asked without ever losing the total.
 *
 * Empty string, never a placeholder, when nothing can be said -- the
 * caller appends this to a chip label and a dash reads as a value.
 */
export function jarsLabel({ split, sizeId, varietalId, showVarietals = true }) {
  const total = jarsInSize(split, sizeId);
  if (total === null) return "";
  if (!showVarietals || varietalId === undefined) return ` (${total})`;

  const mine = jarsOfVarietalInSize(split, sizeId, varietalId);
  return ` (${mine} of ${total})`;
}

/**
 * The shelf, as rows worth rendering: size x varietal, with counts.
 *
 * Sizes holding nothing are dropped. A grid of every size against every
 * varietal is mostly noise, and a zero for a combination that has never
 * existed reads as a loss rather than an absence -- the same argument
 * /honey/bottled-stock makes for omitting zero varietal rows.
 */
export function shelfRows(split, sizes = [], bySize = null) {
  /* THE SPLIT IS NOT THE ONLY SOURCE. It is the RICHER one, so it wins
     when it is there -- but an account that never separates varietals
     has no split to load, and a client whose API predates the split
     gets nothing back for it. Both still have a shelf, and both were
     shown an empty one (web) or no card at all (mobile).

     Falling back keeps "unknown is not zero" intact, which is the rule
     this file is built on: null still means NOBODY HAS ANSWERED YET,
     and that is now only true when NEITHER source has arrived. A zero
     we did not verify reads as "none left" and stops somebody sending
     jars they are holding. */
  if (!split) {
    if (!bySize) return null;
    return bySize
      .map((r) => ({
        bottle_size_id: r.bottle_size_id,
        label: r.label,
        total: Number(r.bottles || 0),
        // No split to show, which is not the same as a split of one.
        varietals: [],
      }))
      .filter((row) => row.total > 0);
  }
  return sizes
    .map((s) => ({
      bottle_size_id: s.bottle_size_id,
      label: s.label,
      total: jarsInSize(split, s.bottle_size_id) || 0,
      varietals: split
        .filter((r) => String(r.bottle_size_id) === String(s.bottle_size_id)
          && Number(r.bottles || 0) > 0)
        .map((r) => ({
          varietal_id: r.varietal_id ?? null,
          name: r.varietal_name || "Unspecified",
          bottles: Number(r.bottles || 0),
        })),
    }))
    .filter((row) => row.total > 0);
}
